import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import { apiGetLotteryDetailedResults } from '@homeplay/api';
import { useCallOnceWhen } from '../../hooks/useCallOnceWhen';
import { useAssistantIntents } from '../../hooks/useLottoAssistantContext';
import { formatLottoDate, parseDateArg } from '../../utils/date';
import LotteryCard from '../LotteryCard';
import WidgetError from '../WidgetError';
import DateRangeFilter from '../../../../../components/src/UI/DateRangeFilter';
import RefreshButton from '../RefreshButton';
import ResultBalls from '../ResultBalls';
import BotMessage from '../BotMessage';
import classes from './LotteryDrawHistory.module.scss';

const LotteryDrawHistory = ({ intentArgs, onDataLoaded }) => {
  const unitToken = useSelector(selectUnitToken);
  const { handleLotterySelect, handleOutcomeSelect } = useAssistantIntents();
  const prefetchedData = intentArgs?.data;
  const lotteryId = intentArgs?.lottery_id;
  const intentFrom = intentArgs?.from;
  const intentTo = intentArgs?.to;

  const parsedIntentFrom = useMemo(() => parseDateArg(intentFrom), [intentFrom]);
  const parsedIntentTo = useMemo(() => parseDateArg(intentTo, true), [intentTo]);

  const [allResults, setAllResults] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    from: parsedIntentFrom,
    to: parsedIntentTo,
  });
  const [ignorePrefetchedData, setIgnorePrefetchedData] = useState(false);

  const [data, setData] = useState(prefetchedData ?? null);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState(null);

  const hasDateFilter = dateFilter.from !== null || dateFilter.to !== null;
  const hasPrefetched = !!prefetchedData && !hasDateFilter && !ignorePrefetchedData;

  useEffect(() => {
    setDateFilter((prev) => {
      if (prev.from === parsedIntentFrom && prev.to === parsedIntentTo) return prev;
      return { from: parsedIntentFrom, to: parsedIntentTo };
    });
  }, [parsedIntentFrom, parsedIntentTo]);

  const fetchData = useCallback(async () => {
    if (!unitToken || !lotteryId || hasPrefetched) return;
    setIsFetching(true);
    setError(null);
    try {
      const result = await apiGetLotteryDetailedResults({
        unitToken,
        lotteryId,
        limit: 12,
        from: dateFilter.from,
        to: dateFilter.to,
      });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsFetching(false);
    }
  }, [unitToken, lotteryId, dateFilter.from, dateFilter.to, hasPrefetched]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resolvedData = hasPrefetched ? prefetchedData : data;
  const resultHistory = resolvedData?.lottery_results;
  const isDataLoaded = !!resolvedData || !!error;
  useCallOnceWhen(isDataLoaded, onDataLoaded);

  useEffect(() => {
    if (!resultHistory) return;
    setAllResults(resultHistory.results ?? []);
    setCursor(resultHistory.pagination?.next_cursor ?? null);
    setHasMore(resultHistory.pagination?.has_more ?? false);
  }, [resultHistory]);

  const handleLoadMore = async () => {
    if (!cursor || !lotteryId || !unitToken) return;
    setIsFetchingMore(true);
    try {
      const result = await apiGetLotteryDetailedResults({
        unitToken,
        lotteryId,
        limit: 12,
        before: cursor,
        from: dateFilter.from,
        to: dateFilter.to,
      });
      const nextHistory = result?.lottery_results;
      if (nextHistory) {
        setAllResults((prev) => [...prev, ...(nextHistory.results ?? [])]);
        setCursor(nextHistory.pagination?.next_cursor ?? null);
        setHasMore(nextHistory.pagination?.has_more ?? false);
      }
    } finally {
      setIsFetchingMore(false);
    }
  };

  const handleRefresh = () => {
    if (hasPrefetched) {
      setIgnorePrefetchedData(true);
    } else if (lotteryId) {
      fetchData();
    }
  };

  if (!prefetchedData && error) {
    return <WidgetError onRetry={fetchData} error={error} />;
  }

  const emptyText = hasDateFilter
    ? 'No results found for the selected date range'
    : 'No results found for this lottery';

  return (
    <div className={classes.Container}>
      {resultHistory && (
        <div className={classes.LotteryCardWrapper}>
          <LotteryCard
            lottery={resultHistory}
            onSelect={handleLotterySelect}
            onOutcomeSelect={handleOutcomeSelect}
          />
        </div>
      )}

      <div className={classes.HeaderActions}>
        <DateRangeFilter
          title="Draw Date"
          initialFrom={dateFilter.from}
          initialTo={dateFilter.to}
          popupAlign="left"
          onApply={(from, to) =>
            setDateFilter({ from: from ?? null, to: to ?? null })
          }
          onClear={() => setDateFilter({ from: null, to: null })}
        />
        <RefreshButton onClick={handleRefresh} isFetching={isFetching} ariaLabel="Refresh history" />
      </div>

      {allResults.length === 0 ? (
        <BotMessage content={emptyText} withAvatar={false} />
      ) : (
        <div className={classes.ResultsGrid}>
          {allResults.map((result, index) => {
            const resultsArray = Array.isArray(result.balls) ? result.balls : [];
            return (
              <div key={`${result.draw_number}-${index}`} className={classes.ResultCard}>
                <div className={classes.ResultHeader}>
                  <span className={classes.DrawLabel}>Draw Result</span>
                  <span className={classes.DrawDate}>
                    {formatLottoDate(result.draw_date).replace(' ', ' · ')}
                  </span>
                </div>
                <hr className={classes.Divider} />
                {resultsArray.length > 0 && <ResultBalls numbers={resultsArray} />}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className={classes.LoadMoreWrapper}>
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={isFetchingMore}
            className={classes.LoadMoreButton}
          >
            {isFetchingMore ? 'Loading…' : 'Show More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default LotteryDrawHistory;
